import React, { useState, useMemo, useEffect } from 'react';

import { Table, Row, Col, Button, Collapse, Card, CardBody, CardHeader } from 'reactstrap'

import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import ExportToExcel from './exportExcel';
import { QuickPreview } from './clickQuickPreview';

export const HistoryTable = ({ history }) => {
    const [openOrders, setOpenOrders] = useState({});
    const [quickFilterText, setQuickFilterText] = useState('');

    const toggleOrders = (bus_id) => {
        setOpenOrders(prevState => ({
            ...prevState,
            [bus_id]: !prevState[bus_id]
        }));
    };

    useEffect(() => {
        setOpenOrders({})
    }, [history])

    // Transform history data to fit dataMyTable format
    const dataMyTable = Object.keys(history).reverse().flatMap(date => 
        history[date].map(bus => ({
            date,
            bus: bus.bus,
            terminal: bus.terminal,
            skids: bus.skids,
            weight: `${bus.weight} (${bus.p_weight}%)`,
            length: `${bus.total_length} (${bus.p_length}%)`,
            height: bus.height,
            orders: bus.orders,
            bus_id: bus.bus_id // Add bus_id for unique row identification
        }))
    );

    const columnDefs = useMemo(() => [
        { headerName: "Date", field: "date", filter: "agDateFilter", floatingFilter: true },
        { headerName: "Bus", field: "bus", filter: "agTextColumnFilter", floatingFilter: true },
        { headerName: "Terminal", field: "terminal", filter: "agTextColumnFilter", floatingFilter: true },
        { headerName: "Skids", field: "skids", filter: "agNumberColumnFilter", floatingFilter: true },
        { headerName: "Weight", field: "weight", filter: "agTextColumnFilter", floatingFilter: true },
        { headerName: "Length", field: "length", filter: "agTextColumnFilter", floatingFilter: true },
        { headerName: "Height", field: "height", filter: "agNumberColumnFilter", floatingFilter: true },
        {
            headerName: "Orders",
            field: "orders",
            cellRenderer: (params) => (
                <button 
                    onClick={() => toggleOrders(params.data.bus_id)}
                    className={ `btn btn-xs ${openOrders[params.data.bus_id] ? 'btn-danger bg-danger' : ''}`
                }
                    key={params.data.bus_id}
                >
                    {openOrders[params.data.bus_id] ? 'Close Orders' : `See Orders (${params.value.length})`}
                </button>
            ),
            filter: false,
            floatingFilter: false
        }
    ], [openOrders]);

    const defaultColDef = useMemo(() => ({
        flex: 1,
        minWidth: 100,
        filter: true,
        floatingFilter: true,
    }), []);
    
    const OrdersPreview = (
        <>
        {dataMyTable.map(bus => (
            <Collapse 
                key={bus.bus_id} 
                isOpen={openOrders[bus.bus_id]}
                id={'orders_card' + bus.bus_id}
                className='m-0 p-0'
            >
                <Card className='p-0 shadow rounded'>
                    <CardHeader className='bg-danger text-white p-1 text-center'>
                        <div className='row m-0'>
                            <div className='col d-flex pt-1 pb-0 mb-0'>
                                {bus.bus} - {bus.date}
                            </div>
                            <div className='col d-flex justify-content-end'>
                                <Button 
                                    onClick={() => toggleOrders(bus.bus_id)} 
                                    className='btn-xs bg-dark border-dark round round-2 text-white'>
                                    <strong>X</strong>
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardBody className='p-0 m-0'>
                        <Table className='p-0 m-0' borderless striped>
                            <thead>
                                <tr>
                                    <th>Order</th>
                                    <th>Customer</th>
                                    <th>Weight</th>
                                    <th>Skids</th>
                                    <th>Partials</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bus.orders && bus.orders.map(order => (
                                    <tr className="border sm" key={order.order_no}>
                                        <td>{order.order_no}-{order.order_group}</td>
                                        <td>{order.customer}</td>
                                        <td>{order.weight}</td>
                                        <td>
                                            {order.units}
                                            {order.partial_count > 0 ? ` (${order.partial_skids})` : <></>}
                                        </td>
                                        <td>{order.partial_count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>    
            </Collapse>
        ))}
        </>
    )

    const BusesTable = useMemo(() => {
        return (
            <div className="ag-theme-alpine custom-grid" style={{ height: '90vh', width: '100%' }}>
                <AgGridReact
                    rowData={dataMyTable}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    pagination={true}
                    paginationPageSize={17}
                    quickFilterText={quickFilterText}
                />
            </div>
        )
    }, [history, openOrders]);

    return (
        <>
        <QuickPreview history={history} />
        <Table style={{ borderRadius: 8 }}>
            <thead className="bg-dark text-white">
                <th>
                    Table of Buses By Pickup Date
                </th>
                <th>
                    <Row>
                        <Col>
                            {Object.values(openOrders).every(value => value === false) ? 
                                (<></>):(<div className='p-0 m-0'>Orders Preview</div>)}
                        </Col>
                        <Col className='d-flex justify-content-end p-0 m-0 pr-3'><ExportToExcel history={history}/></Col>
                    </Row>
                </th>
            </thead>
            <tbody className="m-0 mt-1 p-1">
                <tr style={{ height: '90vh' }}>
                    {Object.values(openOrders).every(value => value === false) ? (
                        <>
                            <td colSpan={2} style={{ verticalAlign: 'top', padding: 0, width: '100%' }}>
                                <div className='p-0' style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '90vh', width: '100%', whiteSpace: 'nowrap' }}>
                                    {BusesTable}
                                </div>
                            </td>
                        </>
                    ) : (
                        <>
                            <td className="col-9" style={{ verticalAlign: 'top', padding: 0, width: '80vw' }}>
                                <div className='p-0' style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '90vh', width: '100%', whiteSpace: 'nowrap' }}>
                                    {BusesTable}
                                </div>
                            </td>
                            <td className="col-3" style={{ verticalAlign: 'top', padding: 0, width: '20vw', maxWidth: '20%'}}>
                                <div className='p-2 pb-0 mb-0' style={{ maxWidth: '100%', overflowY: 'auto', overflowX: 'hidden', maxHeight: '90vh', width: '100%', whiteSpace: 'nowrap' }}>
                                    {OrdersPreview}
                                </div>
                            </td>
                        </>
                    )}
                </tr>
            </tbody>
        </Table>
        </>
    );
};
